// Templates de figures géométriques rapides pour l'éditeur GeoGebra
// Chaque template génère une figure via l'API GeoGebra

// Helper : applique couleur noire + taille 4 à une liste de points
const stylePoints = (api, names) => {
  for (const name of names) {
    api.evalCommand(`SetPointSize(${name}, 4)`);
    api.evalCommand(`SetColor(${name}, 0, 0, 0)`);
  }
};

// Helper : applique couleur + épaisseur à des objets
const styleLines = (api, items) => {
  for (const { name, color = [0, 0, 0], thickness = 4, style } of items) {
    api.evalCommand(`SetColor(${name}, ${color.join(', ')})`);
    api.evalCommand(`SetLineThickness(${name}, ${thickness})`);
    if (style !== undefined) api.evalCommand(`SetLineStyle(${name}, ${style})`);
  }
};

export const quickTemplates = [
  {
    id: 'cercle',
    icon: '⊙',
    label: 'Cercle',
    generate: (api) => {
      api.evalCommand('O = (0, 0)');
      api.evalCommand('c = Circle(O, 3)');
      styleLines(api, [{ name: 'c' }]);
      stylePoints(api, ['O']);
    }
  },
  {
    id: 'triangle',
    icon: '△',
    label: 'Triangle',
    generate: (api) => {
      api.evalCommand('A = (0, 0)');
      api.evalCommand('B = (5, 0)');
      api.evalCommand('C = (2.5, 4)');
      api.evalCommand('poly = Polygon(A, B, C)');
      styleLines(api, [{ name: 'poly' }]);
      stylePoints(api, ['A', 'B', 'C']);
    }
  },
  {
    id: 'rectangle',
    icon: '▭',
    label: 'Rectangle',
    generate: (api) => {
      api.evalCommand('A = (0, 0)');
      api.evalCommand('B = (5, 0)');
      api.evalCommand('C = (5, 3)');
      api.evalCommand('D = (0, 3)');
      api.evalCommand('poly = Polygon(A, B, C, D)');
      styleLines(api, [{ name: 'poly' }]);
      stylePoints(api, ['A', 'B', 'C', 'D']);
    }
  },
  {
    id: 'segment',
    icon: '—',
    label: 'Segment',
    generate: (api) => {
      api.evalCommand('A = (-3, 0)');
      api.evalCommand('B = (3, 0)');
      api.evalCommand('s = Segment(A, B)');
      styleLines(api, [{ name: 's' }]);
      stylePoints(api, ['A', 'B']);
    }
  },
  {
    id: 'angle',
    icon: '∠',
    label: 'Angle',
    generate: (api) => {
      api.evalCommand('A = (3, 0)');
      api.evalCommand('B = (0, 0)');
      api.evalCommand('C = (2, 3)');
      api.evalCommand('r1 = Ray(B, A)');
      api.evalCommand('r2 = Ray(B, C)');
      api.evalCommand('alpha = Angle(A, B, C)');
      styleLines(api, [{ name: 'r1' }, { name: 'r2' }]);
      stylePoints(api, ['A', 'B', 'C']);
    }
  },
  {
    id: 'paralleles',
    icon: '∥',
    label: 'Parallèles',
    generate: (api) => {
      api.evalCommand('A = (-4, 1)');
      api.evalCommand('B = (4, 1)');
      api.evalCommand('d1 = Line(A, B)');
      api.evalCommand('C = (0, -2)');
      api.evalCommand('d2 = Line(C, d1)');
      styleLines(api, [
        { name: 'd1', color: [255, 0, 0] },
        { name: 'd2', color: [0, 0, 255] },
      ]);
      stylePoints(api, ['A', 'B', 'C']);
    }
  },
  {
    id: 'perpendiculaires',
    icon: '⊥',
    label: 'Perpendiculaires',
    generate: (api) => {
      api.evalCommand('A = (-4, 0)');
      api.evalCommand('B = (4, 0)');
      api.evalCommand('d1 = Line(A, B)');
      api.evalCommand('C = (0, 0)');
      api.evalCommand('d2 = PerpendicularLine(C, d1)');
      styleLines(api, [
        { name: 'd1', color: [255, 0, 0] },
        { name: 'd2', color: [0, 0, 255] },
      ]);
      stylePoints(api, ['A', 'B', 'C']);
    }
  },
  {
    id: 'symetrie_axiale',
    icon: '⿻',
    label: 'Sym. axiale',
    generate: (api) => {
      api.evalCommand('A = (-3, 1)');
      api.evalCommand('B = (-1, 3)');
      api.evalCommand('C = (-3, 3)');
      api.evalCommand('poly = Polygon(A, B, C)');
      api.evalCommand('axe = yAxis');
      api.evalCommand("A' = Reflect(A, axe)");
      api.evalCommand("B' = Reflect(B, axe)");
      api.evalCommand("C' = Reflect(C, axe)");
      api.evalCommand("poly2 = Polygon(A', B', C')");
      styleLines(api, [
        { name: 'poly', color: [30, 58, 138], thickness: 3 },
        { name: 'poly2', color: [6, 182, 212], thickness: 3 },
      ]);
      stylePoints(api, ['A', 'B', 'C']);
      stylePoints(api, ["A'", "B'", "C'"]);
    }
  },
  {
    id: 'symetrie_centrale',
    icon: '⟲',
    label: 'Sym. centrale',
    generate: (api) => {
      api.evalCommand('A = (-3, 1)');
      api.evalCommand('B = (-1, 1)');
      api.evalCommand('C = (-2, 3)');
      api.evalCommand('O = (0, 0)');
      api.evalCommand('poly = Polygon(A, B, C)');
      api.evalCommand("A' = Reflect(A, O)");
      api.evalCommand("B' = Reflect(B, O)");
      api.evalCommand("C' = Reflect(C, O)");
      api.evalCommand("poly2 = Polygon(A', B', C')");
      styleLines(api, [
        { name: 'poly', color: [30, 58, 138], thickness: 3 },
        { name: 'poly2', color: [6, 182, 212], thickness: 3 },
      ]);
      stylePoints(api, ['A', 'B', 'C', 'O']);
      stylePoints(api, ["A'", "B'", "C'"]);
    }
  },
  {
    id: 'mediatrice',
    icon: '⊥̃',
    label: 'Médiatrice',
    generate: (api) => {
      api.evalCommand('A = (-2, 0)');
      api.evalCommand('B = (4, 0)');
      api.evalCommand('s = Segment(A, B)');
      api.evalCommand('M = Midpoint(A, B)');
      api.evalCommand('med = PerpendicularBisector(A, B)');
      styleLines(api, [
        { name: 's' },
        { name: 'med', style: 2 },
      ]);
      stylePoints(api, ['A', 'B', 'M']);
    }
  },
  {
    id: 'thales',
    icon: '⋈',
    label: 'Thalès',
    generate: (api) => {
      api.evalCommand('O = (0, 0)');
      api.evalCommand('A = (4, 2)');
      api.evalCommand('B = (4, -2)');
      api.evalCommand('M = (2.4, 1.2)');
      api.evalCommand('N = (2.4, -1.2)');
      api.evalCommand('sOA = Segment(O, A)');
      api.evalCommand('sOB = Segment(O, B)');
      api.evalCommand('sAB = Segment(A, B)');
      api.evalCommand('sMN = Segment(M, N)');
      styleLines(api, [
        { name: 'sOA' },
        { name: 'sOB' },
        { name: 'sAB' },
        { name: 'sMN', style: 2 },
      ]);
      stylePoints(api, ['O', 'A', 'B', 'M', 'N']);
    }
  },
];
